import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { hubClient } from '../../services/hubClient';
import { errorText } from '../../lib/assistantView';
import { docLink, latestGate } from '../../lib/answerReviewView';
import type { BotRef, BotSystem } from '../../lib/botNav';
import { fmtTime } from '../../lib/jobView';
import { ACCEPT_BATCH, REJECT_BATCH, acceptMessage, acceptPathText, chunks, undoConfirmText, undoMessage, type Suggestion, type SuggestionStatus } from '../../lib/testerView';

const STATUSES: { key: SuggestionStatus; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'reverted', label: 'Undone' },
];

interface Note {
  id: string;
  ok: boolean;
  text: string;
  /** The FAQ document an accept created (builder-chunk suggestions) — linked in the note. */
  faqDocumentId?: string;
}

/**
 * Quality › Suggestions (spec §4.4): better text for the documents behind wrong test answers. Side by side; accept or
 * reject one or many; Undo an accepted one. Each item says what Accept does (spec §3.8): a hand-written document's text
 * is replaced (the old text kept for Undo); a website-page chunk is left alone and an FAQ document is added (Undo deletes
 * it). A suggestion whose document changed since is shown but cannot be accepted.
 */
export function SuggestionsPanel({ system, botId }: { system: BotSystem; botId: string }) {
  const bot = useMemo<BotRef>(() => ({ system, botId }), [system, botId]);
  const [status, setStatus] = useState<SuggestionStatus>('pending');
  const [items, setItems] = useState<Suggestion[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [notes, setNotes] = useState<Note[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gate = useRef(latestGate());

  const load = useCallback(async () => {
    const id = gate.current.begin();
    setItems(null);
    setSelected(new Set());
    try {
      const r = await hubClient.testerSuggestions(bot, status);
      if (gate.current.isCurrent(id)) setItems(r.items);
    } catch (e) {
      if (!gate.current.isCurrent(id)) return;
      setItems([]);
      setError(errorText(e));
    }
  }, [bot, status]);
  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<Note[]>) {
    setBusy(true);
    setError(null);
    setNotes([]);
    try {
      setNotes(await fn());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
      await load();
    }
  }

  const accept = (ids: string[]) =>
    run(async () => {
      const out: Note[] = [];
      for (const part of chunks(ids, ACCEPT_BATCH)) {
        const r = await hubClient.testerAccept(bot, part);
        for (const x of r.results) {
          out.push({ id: x.id, ok: x.ok && x.marked !== false && x.reindexed !== false, text: acceptMessage(x), faqDocumentId: x.faqDocumentId });
        }
      }
      return out;
    });
  const reject = (ids: string[]) =>
    run(async () => {
      let rejected = 0;
      let skipped = 0;
      for (const part of chunks(ids, REJECT_BATCH)) {
        const r = await hubClient.testerReject(bot, part);
        rejected += r.rejected;
        skipped += r.skipped.length;
      }
      return [{ id: 'reject', ok: true, text: `${rejected} rejected${skipped ? ` · ${skipped} no longer pending` : ''}.` }];
    });
  const undo = (s: Suggestion) => {
    if (!window.confirm(undoConfirmText(s))) return;
    void run(async () => {
      const r = await hubClient.testerUndo(bot, s.id);
      return [{ id: s.id, ok: r.marked !== false && r.reindexed !== false, text: undoMessage(r) }];
    });
  };
  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const pending = status === 'pending';
  const selectable = (items ?? []).filter((s) => s.status === 'pending' && !s.stale && s.document.exists);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <div className="inline-flex overflow-hidden rounded border border-gray-300">
          {STATUSES.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => {
                setNotes([]);
                setStatus(o.key);
              }}
              className={`px-3 py-1 ${status === o.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {pending && selectable.length > 0 && (
          <>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={selected.size === selectable.length}
                onChange={(e) => setSelected(e.target.checked ? new Set(selectable.map((s) => s.id)) : new Set())}
              />
              All
            </label>
            <button
              type="button"
              disabled={busy || selected.size === 0}
              className="px-3 py-1 rounded bg-green-600 text-white disabled:opacity-50"
              onClick={() => void accept([...selected])}
            >
              Accept {selected.size || ''}
            </button>
            <button
              type="button"
              disabled={busy || selected.size === 0}
              className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
              onClick={() => void reject([...selected])}
            >
              Reject {selected.size || ''}
            </button>
          </>
        )}
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}
      {notes.length > 0 && (
        <ul className="text-sm space-y-0.5">
          {notes.map((n) => (
            <li key={n.id} className={n.ok ? 'text-green-700' : 'text-amber-800'}>
              {n.text}
              {n.faqDocumentId && (
                <>
                  {' '}
                  <Link className="text-blue-600 hover:underline" to={docLink(system, botId, n.faqDocumentId)}>
                    Open the FAQ document
                  </Link>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {items === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">{pending ? 'No suggestions waiting. Run a test and press “Suggest fixes” on its page.' : 'Nothing here.'}</p>
      ) : (
        <ul className="space-y-3">
          {items.map((s) => (
            <li key={s.id} className="bg-white border border-gray-200 rounded-lg p-3 text-sm space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {pending && <input type="checkbox" disabled={s.stale || !s.document.exists} checked={selected.has(s.id)} onChange={() => toggle(s.id)} />}
                {s.document.exists ? (
                  <Link className="font-medium text-blue-600 hover:underline" to={docLink(system, botId, s.document.id)}>
                    {s.document.title || `Document ${s.document.id}`}
                  </Link>
                ) : (
                  <span className="text-gray-400">Document {s.document.id} (no longer exists)</span>
                )}
                {s.stale && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-800">Document changed since</span>}
                <span className="text-xs text-gray-500">
                  {fmtTime(s.createdAt)}
                  {s.decidedBy ? ` · ${s.status} by ${s.decidedBy} ${fmtTime(s.decidedAt)}` : ''}
                </span>
                <span className="ml-auto flex gap-3">
                  {s.status === 'pending' && (
                    <>
                      <button
                        type="button"
                        disabled={busy || s.stale || !s.document.exists}
                        className="text-green-700 hover:underline disabled:text-gray-400"
                        onClick={() => void accept([s.id])}
                      >
                        Accept
                      </button>
                      <button type="button" disabled={busy} className="text-gray-600 hover:underline" onClick={() => void reject([s.id])}>
                        Reject
                      </button>
                    </>
                  )}
                  {s.status === 'accepted' &&
                    (s.undoable ? (
                      <button type="button" disabled={busy} className="text-blue-600 hover:underline" onClick={() => undo(s)}>
                        Undo
                      </button>
                    ) : (
                      <span className="text-xs text-gray-500">No undo (accepted outside the hub, or edited since)</span>
                    ))}
                </span>
              </div>
              {s.failedQuestion && (
                <div>
                  <span className="text-gray-500">Failed question:</span> {s.failedQuestion}
                </div>
              )}
              <div className={`text-xs ${s.acceptPath === 'faq' ? 'text-blue-800' : 'text-gray-600'}`}>
                {acceptPathText(s)}
                {s.faqDocument && (
                  <>
                    {' — '}
                    <Link className="text-blue-600 hover:underline" to={docLink(system, botId, s.faqDocument.id)}>
                      {s.faqDocument.title || `Document ${s.faqDocument.id}`}
                    </Link>
                  </>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1">{s.status === 'pending' ? 'Now' : 'Before'}</div>
                  <pre className="whitespace-pre-wrap bg-gray-50 border rounded p-2 text-xs max-h-80 overflow-y-auto">{s.oldContent}</pre>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1">{s.status === 'pending' ? 'Suggested' : 'Suggested text'}</div>
                  <pre className="whitespace-pre-wrap bg-green-50 border rounded p-2 text-xs max-h-80 overflow-y-auto">{s.proposedContent}</pre>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
